import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaArchive,
  FaInbox,
  FaSort,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaDollarSign,
  FaUndo,
} from 'react-icons/fa';
import { getCostItems, deleteCostItem, updateCostItem } from './costItemsService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import Pagination from '../../components/shared/Pagination';
import { ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  LoadingCard,
  FilterSection,
  FilterGroup,
  FilterLabel,
  FilterButton,
} from '../../components/styled/GridComponents';

const CATEGORIES = ['Products', 'Materials', 'Crew Hours', 'Maintenance', 'Tools', 'Fuel', 'Others'];

export default function CostItemList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const navigate = useNavigate();

  const showNotification = (message, isSuccess = true) => {
    setFeedbackMessage(message);
    setFeedbackSuccess(isSuccess);
    setShowFeedback(true);
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await getCostItems({ includeInactive: showInactive });
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching cost items:', error);
      showNotification('Failed to load cost items.', false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredItems = useMemo(() => {
    let list = items;
    if (categoryFilter !== 'All') {
      list = list.filter((it) => it.category === categoryFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (it) =>
          (it.name || '').toLowerCase().includes(q) ||
          (it.description || '').toLowerCase().includes(q) ||
          (it.unit || '').toLowerCase().includes(q),
      );
    }
    const sorted = [...list].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.field === 'default_unit_price') {
        return (parseFloat(a.default_unit_price) - parseFloat(b.default_unit_price)) * dir;
      }
      const av = (a[sortConfig.field] || '').toString().toLowerCase();
      const bv = (b[sortConfig.field] || '').toString().toLowerCase();
      return av.localeCompare(bv) * dir;
    });
    return sorted;
  }, [items, categoryFilter, searchQuery, sortConfig]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirst, indexOfLast);

  const handleArchive = async () => {
    try {
      await deleteCostItem(itemToArchive.cost_item_id);
      await fetchItems();
      showNotification(`"${itemToArchive.name}" archived. It will no longer appear in the picker.`, true);
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Failed to archive cost item.';
      showNotification(msg, false);
    } finally {
      setArchiveModalOpen(false);
      setItemToArchive(null);
    }
  };

  const handleRestore = async (item) => {
    try {
      await updateCostItem(item.cost_item_id, { isActive: true });
      await fetchItems();
      showNotification(`"${item.name}" restored.`, true);
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Failed to restore cost item.';
      showNotification(msg, false);
    }
  };

  const formatPrice = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return '$0.00';
    return `$${n.toFixed(2)}`;
  };

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Cost Items"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/cost-items/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Category:</FilterLabel>
          <FilterButton isActive={categoryFilter === 'All'} onClick={() => setCategoryFilter('All')}>
            All
          </FilterButton>
          {CATEGORIES.map((cat) => (
            <FilterButton
              key={cat}
              isActive={categoryFilter === cat}
              onClick={() => setCategoryFilter(cat)}
            >
              {cat}
            </FilterButton>
          ))}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton isActive={sortConfig.field === 'name'} onClick={() => handleSort('name')}>
            {sortConfig.field === 'name'
              ? sortConfig.direction === 'asc'
                ? <FaSortAlphaDown />
                : <FaSortAlphaUp />
              : <FaSort />}
            Name
          </FilterButton>
          <FilterButton
            isActive={sortConfig.field === 'default_unit_price'}
            onClick={() => handleSort('default_unit_price')}
          >
            {sortConfig.field === 'default_unit_price'
              ? sortConfig.direction === 'asc'
                ? <FaSortAlphaDown />
                : <FaSortAlphaUp />
              : <FaSort />}
            Price
          </FilterButton>
        </FilterGroup>

        <FilterGroup>
          <FilterButton isActive={showInactive} onClick={() => setShowInactive((v) => !v)}>
            <FaArchive /> {showInactive ? 'Hide archived' : 'Show archived'}
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <LoadingCard key={i}>
              <CardHeader>
                <div style={{ height: '24px', width: '60%' }} />
              </CardHeader>
              <CardContent>
                <div style={{ height: '20px', width: '80%' }} />
                <div style={{ height: '20px', width: '40%' }} />
              </CardContent>
            </LoadingCard>
          ))
        ) : filteredItems.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No cost items found. Add one to start building your reusable catalog.</p>
          </EmptyState>
        ) : (
          currentItems.map((item) => (
            <Card key={item.cost_item_id} style={item.isActive === false ? { opacity: 0.6 } : undefined}>
              <CardHeader>
                <h3>{item.name}</h3>
                <div className="price">
                  <FaDollarSign />
                  {formatPrice(item.default_unit_price).replace('$', '')}
                  {item.unit ? ` / ${item.unit}` : ''}
                </div>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <strong>Category:</strong> {item.category}
                </InfoRow>
                {item.description && (
                  <InfoRow>
                    <p>{item.description}</p>
                  </InfoRow>
                )}
                {item.isActive === false && (
                  <InfoRow>
                    <em style={{ color: '#aa6600' }}>Archived — hidden from job cost picker.</em>
                  </InfoRow>
                )}
              </CardContent>

              <ActionIconsContainer>
                <ActionButton
                  onClick={() => navigate(`/cost-items/edit/${item.cost_item_id}`)}
                  title="Edit cost item"
                >
                  <FaEdit />
                </ActionButton>
                {item.isActive === false ? (
                  <ActionButton onClick={() => handleRestore(item)} title="Restore cost item">
                    <FaUndo />
                  </ActionButton>
                ) : (
                  <ActionButton
                    onClick={() => {
                      setItemToArchive(item);
                      setArchiveModalOpen(true);
                    }}
                    title="Archive cost item"
                  >
                    <FaTrash />
                  </ActionButton>
                )}
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {!loading && filteredItems.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredItems.length}
          paginate={setCurrentPage}
          currentPage={currentPage}
        />
      )}

      <Modal
        isOpen={archiveModalOpen}
        title="Archive Cost Item"
        message={
          itemToArchive
            ? `Archive "${itemToArchive.name}"? Past job costs that used it stay unchanged. It will be hidden from the picker on new entries.`
            : ''
        }
        onConfirm={handleArchive}
        onCancel={() => {
          setArchiveModalOpen(false);
          setItemToArchive(null);
        }}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)}
      />
    </DashboardLayout>
  );
}
